import React, { useEffect, useState, useRef, ReactNode } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams, useParams, useNavigate } from 'react-router-dom'
import Form from 'react-bootstrap/Form';
import { useForm, Controller, useFormContext, useFieldArray } from "react-hook-form";
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { InputGroup } from 'react-bootstrap';
import CurrencyInput from 'react-currency-input-field';

import { currencyFormat } from '../../resources/helper'
import "react-datepicker/dist/react-datepicker.css";
import Card from 'react-bootstrap/Card';
import { ReconcileFormI, DepositGlClassificationFormI } from '../../resources/form-props';
import { glClassificationApi } from '../../resources/hooks/api/glClassificationApi';
import { GLSearchFormValidate, BankRevenueValidate, BankReconcileValidate } from '../../resources/form-validator';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import { useChartAccountApi } from '../../resources/hooks/api/chartAccountApiHook';
import { getBranch } from '../../store/user/selectors'

import '../GlClassification/GlClassification.css'
import { GL_VP, MONTHS } from '../../resources/constants';
import { lastYears, calculateMaxHeightContent, formatTransactionDate, usdCurrencyFormat } from '../../resources/functions';
import SAlert from '../../components/SAlert';

interface Props {

}

const BankReconcileTxn: React.FC<Props> = () => {
    const { t } = useTranslation('scenes.bankReconcile');
    const { glReconcileTransactionSubmit, glReconcileTransactionlist } = glClassificationApi();
    const routeParams = useParams();
    const navigate = useNavigate();
    const formRef = useRef<any>()
    const [searchParams, setSearchParams] = useSearchParams();
    const branchData = useSelector(getBranch)

    const [records, setRecords] = useState<any>([]);
    const [reconciliationData, setReconciliationData] = useState<any>(null);
    const [showConfirm, setShowConfirm] = useState<boolean>(false);
    const [bankData, setBankData] = useState<any>({
        bank_name: '',
        bank_number: ''
    });


    const [formParams, setFormParams] = useState<any>({
        glAccount: null,
        beginningBalance: 0,
        endingBalance: '',
        day: new Date().getDay(),
        month: new Date().getMonth(),
        year: new Date().getFullYear(),
        endDate: new Date(),
        expense_amount: 0,
        revenue_amount: 0,
        cleared_balance_amount: 0,
        difference_amount: 0,
        uncleared_balance_amount: 0,

    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [contentMaxHeight, setContentMaxHeight] = useState<number>(0)
    const checkAllRef = useRef<HTMLInputElement>(null);

    const { register, control, trigger, handleSubmit, reset, setValue, getValues, formState: { errors } } = useForm<ReconcileFormI>({
        defaultValues: {
            reconciles: []
        },
        resolver: yupResolver(BankReconcileValidate)
    });
    const { fields, append: formAppend, remove: formRemove } = useFieldArray({
        control,
        name: "reconciles"
    });

    const hasUnreconciled = records.some(
        (record: any) => record.reconciled_id === null || record.reconciled_id === undefined
    );

    const isDraft = Boolean(
        reconciliationData && (reconciliationData.status === 'draft' || reconciliationData.is_draft)
    );
    const isCompleted = Boolean(
        reconciliationData && reconciliationData.status === 'completed' && !hasUnreconciled
    );

    useEffect(() => {
        const data: any = {};
        const openingBalance: string = searchParams.get('beginningBalance') ? searchParams.get('beginningBalance')! : '0';
        const endingBalance: string = searchParams.get('endingBalance') ? searchParams.get('endingBalance')! : '0';

        if (routeParams['coa']) {
            data['glAccount'] = routeParams['coa']
        }
        if (openingBalance && !isNaN(+openingBalance)) {
            data['beginningBalance'] = +openingBalance
        }
        if (endingBalance && !isNaN(+endingBalance)) {
            data['endingBalance'] = endingBalance; // Keep as string or format to string
        }

        if (searchParams.get('endDate')) {
            const endDate = moment(searchParams.get('endDate')).endOf('month')
            data['endDate'] = endDate.format('YYYY-MM-DD')
            data['day'] = endDate.format('DD')
            data['month'] = endDate.format('MM')
            data['year'] = endDate.format('YYYY')
        }

        setFormParams((prevState: any) => ({
            ...prevState,
            ...data
        }))

    }, [routeParams, searchParams])


    useEffect(() => {
        if (formParams['glAccount']) {
            fetchTransactionList();
        }
    }, [formParams['glAccount']])

    useEffect(() => {
        records.map((record: any, index: number) => {
            let isChecked = false;
            if (isDraft) {
                isChecked = record['reconciled_id'] === reconciliationData.id;
            } else if (isCompleted) {
                isChecked = true;
            }

            formAppend({
                formIndex: index,
                checked: isChecked,
                id: record['id'],
                transaction_at: record['transaction']['transaction_at'],
                account_type: record['account_type'],
                description: record['transaction']['description'],
                posted: record['transaction']['updated_at'] ? true : false,
                account_value: record['account_value'],
                reconciled_id: record['reconciled_id']
            })
        })

        calculateSum()
    }, [records, reconciliationData])


    // useEffect(() => {
    //     if (reconciliationData) {
    //         const data: any = {};

    //         data['beginningBalance'] = reconciliationData.opening_balance_amount
    //         data['endingBalance'] = reconciliationData.closing_balance_amount
    //         data['cleared_balance_amount'] = reconciliationData.cleared_balance_amount
    //         data['difference_amount'] = reconciliationData.difference_amount

    //         setFormParams((prevState: any) => ({
    //             ...prevState,
    //             ...data
    //         }))
    //     }

    // }, [reconciliationData])

    useEffect(() => {
        setContentMaxHeight(calculateMaxHeightContent(131));
    })

    useEffect(() => {
        if (checkAllRef.current) {
            const formValues = getValues('reconciles') || [];
            const allChecked = formValues.length > 0 && formValues.every((f: any) => f?.checked);
            checkAllRef.current.checked = allChecked;
        }
    }, [fields])

    const fetchTransactionList = () => {
        const params: any = {
            "page": 1,
            "per_page": 500,
            is_updated: 1,
            is_not_reconciled: 1,
            account_number: formParams['glAccount'],
            branch_id: +branchData['id'],
            month: formParams['month'],
            year: formParams['year']
        }

        setIsLoading(true);
        glReconcileTransactionlist(params, (message: string, resp: any) => {
            reset();
            const recon = resp.data.data.reconciliation;
            setRecords(resp.data.data.items.result);
            setReconciliationData(recon);
            if (recon && (recon.status === 'draft' || recon.is_draft)) {
                setFormParams((prevState: any) => ({
                    ...prevState,
                    beginningBalance: (recon.opening_balance_amount !== null && recon.opening_balance_amount !== undefined) ? recon.opening_balance_amount : prevState.beginningBalance,
                    endingBalance: (recon.closing_balance_amount !== null && recon.closing_balance_amount !== undefined) ? recon.closing_balance_amount : prevState.endingBalance,
                }));
            }
            setBankData({
                bank_name: resp.data.data.bank_name,
                bank_number: resp.data.data.bank_number
            });
            setIsLoading(false);

        }, (message: string, resp: any) => {
            setIsLoading(false);
            toast.error(message);
        })
    }


    const handleCheckAllChange = (e: any) => {
        if (checkAllRef.current) {
            checkAllRef.current.checked = e.target.checked;

            records.map((record: any, index: number) => {
                setValue(`reconciles.${index}.checked`, e.target.checked)
            })

            calculateSum();
        }
    };

    const handleCheckAll = (e: any, index: number) => {
        setValue(`reconciles.${index}.checked`, e.target.checked);
        if (e.target.checked) {
            const checkedRecords = records.filter((record: any, index: number) => getValues(`reconciles.${index}.checked`) === true);

            if (checkedRecords.length == records.length) {
                if (checkAllRef.current) {
                    checkAllRef.current.checked = true;
                }
            }
        } else {
            const uncheckedRecords = records.filter((record: any, index: number) => getValues(`reconciles.${index}.checked`) === false);

            if (uncheckedRecords.length) {
                if (checkAllRef.current) {
                    checkAllRef.current.checked = false;
                }
            }
        }

        calculateSum();
    }

    const calculateSum = () => {
        setIsLoading(true);

        const sumAmount = records.reduce((a: any, c: any, index: number) => {
            const isChecked = getValues(`reconciles.${index}.checked`);

            if (isChecked) {
                if (c['txn_type'] == 1 && c['transaction']['description'] != "Opening Balance") {  // deposit
                    a['revenue'] += +c['account_value'];
                } else if (c['txn_type'] == 2) { // withdrawal
                    a['expense'] += +c['account_value'];
                }
            } else {
                if (c['txn_type'] == 1 && c['transaction']['description'] != "Opening Balance") { // unchecked deposit
                    a['uncleared_revenue'] += +c['account_value'];
                } else if (c['txn_type'] == 2) { // unchecked withdrawal
                    a['uncleared_expense'] += +c['account_value'];
                }
            }

            return a;
        }, { revenue: 0, expense: 0, uncleared_revenue: 0, uncleared_expense: 0 });

        setFormParams((prevState: any) => ({
            ...prevState,
            expense_amount: sumAmount['expense'],
            revenue_amount: sumAmount['revenue'],
            cleared_balance_amount: +prevState['beginningBalance'] - sumAmount['expense'] + sumAmount['revenue'],
            difference_amount: +prevState['beginningBalance'] - sumAmount['expense'] + sumAmount['revenue'] - +prevState['endingBalance'],
            uncleared_balance_amount: sumAmount['uncleared_revenue'] - sumAmount['uncleared_expense']
        }));

        setIsLoading(false);
    }

    const handleClosingBalanceChange = (
        value: string | undefined,
        _name?: string,
        values?: { float: number | null; formatted: string; value: string }
    ) => {
        // Keep raw string or empty string to preserve incomplete inputs like "12."
        const rawVal = value ?? '';
        const numericEndingBalance = values?.float ?? (Number(value) || 0);

        setFormParams((prevState: any) => ({
            ...prevState,
            endingBalance: rawVal,
            difference_amount: +prevState['cleared_balance_amount'] - numericEndingBalance
        }));
    };

    const processData = (data: any) => {
        if (formParams['difference_amount'].toFixed(2) == 0) {
            const records = (data['reconciles'] || [])
                .filter((d: any) => d['checked'])
                .map((d: any) => d.id);

            const formData = {
                gl_account_number: formParams['glAccount'],
                end_date_at: moment(formParams['endDate']).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
                "opening_balance_amount": +formParams['beginningBalance'].toFixed(2),
                "expense_amount": +formParams['expense_amount'].toFixed(2),
                "revenue_amount": +formParams['revenue_amount'].toFixed(2),
                "cleared_balance_amount": +formParams['cleared_balance_amount'].toFixed(2),
                "closing_balance_amount": +(Number(formParams['endingBalance']) || 0).toFixed(2),
                "difference_amount": +formParams['difference_amount'].toFixed(2),
                "account_ids": records,
                "is_draft": false,
                "status": "completed"
            }
            postData(formData)
        } else {
            toast.error(t('message.reconcileError'))
        }
    }

    const handleSaveDraft = () => {
        const formValues = getValues();
        const checkedRecords = (formValues['reconciles'] || [])
            .filter((d: any) => d['checked'])
            .map((d: any) => d.id);

        const formData = {
            gl_account_number: formParams['glAccount'],
            end_date_at: moment(formParams['endDate']).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
            "opening_balance_amount": +formParams['beginningBalance'].toFixed(2),
            "expense_amount": +formParams['expense_amount'].toFixed(2),
            "revenue_amount": +formParams['revenue_amount'].toFixed(2),
            "cleared_balance_amount": +formParams['cleared_balance_amount'].toFixed(2),
            "closing_balance_amount": +(Number(formParams['endingBalance']) || 0).toFixed(2),
            "difference_amount": +formParams['difference_amount'].toFixed(2),
            "account_ids": checkedRecords,
            "is_draft": true,
            "status": "draft"
        };

        setIsLoading(true);
        glReconcileTransactionSubmit({ formData, branch_id: +branchData['id'] }, (message: string, resp: any) => {
            setIsLoading(false);
            toast.success(message || t('message.draftSaved'));
            navigate(-1);
        }, (message: string, resp: any) => {
            setIsLoading(false);
            toast.error(message);
        });
    };

    const postData = (formData: any) => {
        setIsLoading(true);
        glReconcileTransactionSubmit({ formData, branch_id: +branchData['id'] }, (message: string, resp: any) => {
            setIsLoading(false);
            toast.success(message)
            navigate(-1);
        }, (message: string, resp: any) => {
            setIsLoading(false);
            toast.error(message);
        })
    }

    const _handleConfirmation = () => {
        setShowConfirm(false);
        formRef.current.requestSubmit()
    }

    {/* added for totals */ }
    const calculateColumnTotals = () => {
        return records.reduce((acc: any, record: any, index: number) => {
            if (getValues(`reconciles.${index}.checked`)) {
                if (record.txn_type == 1) acc.debit += +record.account_value;
                else if (record.txn_type == 2) acc.credit += +record.account_value;
            }
            return acc;
        }, { debit: 0, credit: 0 });
    };

    const totals = calculateColumnTotals();

    {/* added for totals */ }

    return (
        <div className="main-container flex-grow-1">
            <div className="container-fluid">
                <div className="page-title pb-3 pt-3" id="ux_page_title">
                    <div className="row align-items-center">
                        <div className="col-sm-6 align-items-center d-flex">
                            <h1 className="h5 font-weight-700 mb-0 d-inline-flex">{t('title')}</h1>
                        </div>
                    </div>
                </div>                <div className=" executive-dashboard">
                    <div className="dashboard-top-part">
                        <div className="filter-btn">
                            <div className={`page-loader ${isLoading ? 'visible' : ''}`}>
                                <div className="loader"></div>
                            </div>
                        </div>
                        <Card>
                            <Card.Body>
                                <div className='row'>
                                    <div className="col-md-6">
                                        <span><strong>{t('filter.endDate')}:</strong> {moment(formParams['endDate']).format('LL')}, <strong>
                                            {(() => {
                                                const name = (bankData['bank_name'] || '').trim();
                                                const num = (bankData['bank_number'] || '').trim();
                                                if (name && num) return `${name} ( ${num} )`;
                                                if (name) return name;
                                                if (num) return num;
                                                return t('common.na');
                                            })()}</strong></span>
                                    </div>
                                </div>
                                <div className="row mt-2">
                                    <div className="col-md-8 d-flex justify-content-between">
                                        <div className='d-flex flex-column align-items-center'>
                                            <strong>{t('label.openingBalance')}</strong>
                                            <span className="d-flex align-items-center" style={{ height: '31px' }}>{usdCurrencyFormat(+formParams['beginningBalance'])}</span>
                                        </div>
                                        <div className='d-flex flex-column align-items-center'>
                                            <strong title={t('table.withdrawals.tooltip')}>{t('table.withdrawals')}</strong>
                                            <span className="d-flex align-items-center" style={{ height: '31px' }}>{usdCurrencyFormat(+formParams['expense_amount'])}</span>
                                        </div>
                                        <div className='d-flex flex-column align-items-center'>
                                            <strong title={t('table.deposit.tooltip')}>{t('table.deposit')}</strong>
                                            <span className="d-flex align-items-center" style={{ height: '31px' }}>{usdCurrencyFormat(+formParams['revenue_amount'])}</span>
                                        </div>
                                        <div className='d-flex flex-column align-items-center'>
                                            <strong>{t('label.clearedBalance')}</strong>
                                            <span className="d-flex align-items-center" style={{ height: '31px' }}>{usdCurrencyFormat(+formParams['cleared_balance_amount'])}</span>
                                        </div>
                                        <div className='d-flex flex-column align-items-center'>
                                            <strong>{t('label.unclearedBalance')}</strong>
                                            <span className="d-flex align-items-center" style={{ height: '31px' }}>
                                                {formParams['uncleared_balance_amount'] < 0
                                                    ? `(${usdCurrencyFormat(Math.abs(+formParams['uncleared_balance_amount']))})`
                                                    : usdCurrencyFormat(+formParams['uncleared_balance_amount'])}
                                            </span>
                                        </div>
                                        <div className='d-flex flex-column align-items-center'>
                                            <strong>{t('label.closingBalance')}</strong>
                                            <InputGroup size="sm" style={{ width: '140px', height: '31px' }}>
                                                <InputGroup.Text>$</InputGroup.Text>
                                                <CurrencyInput
                                                    className="form-control"
                                                    placeholder="0.00"
                                                    value={formParams['endingBalance']}
                                                    onValueChange={handleClosingBalanceChange}
                                                    allowDecimals
                                                    decimalsLimit={2}
                                                    decimalSeparator="."
                                                    groupSeparator=","
                                                    disableAbbreviations
                                                    allowNegativeValue={true}
                                                    disabled={isCompleted}
                                                />
                                            </InputGroup>
                                        </div>
                                        <div className='d-flex flex-column align-items-center'>
                                            <strong>{t('label.difference')}</strong>
                                            <span className="d-flex align-items-center" style={{ height: '31px' }}>{formParams['difference_amount'].toFixed(2) == 0.00 ? usdCurrencyFormat(0) : formParams['difference_amount'] < 0 ? `(${usdCurrencyFormat(Math.abs(+formParams['difference_amount']))})` : usdCurrencyFormat(+formParams['difference_amount'])}</span>
                                        </div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>

                        <Form ref={formRef} onSubmit={handleSubmit(processData)}>
                            {
                                !isCompleted ? (
                                    <div className="modal-footer mt-3 d-flex justify-content-end gap-2">
                                        <button
                                            type="button"
                                            onClick={handleSaveDraft}
                                            className="btn btn-outline-secondary"
                                            disabled={isLoading}
                                        >
                                            {t('button.complete_later')}
                                        </button>
                                        {
                                            fields.length > 0 ? (
                                                <button type="submit" className="btn btn-secondary" disabled={isLoading}>
                                                    {t('button.reconcile')}
                                                </button>
                                            ) : (
                                                <button type="button" onClick={() => setShowConfirm(true)} className="btn btn-secondary" disabled={isLoading}>
                                                    {t('button.reconcile')}
                                                </button>
                                            )
                                        }
                                    </div>
                                ) : null
                            }

                            <br></br>
                            <div className="table-responsive list-table table-scroll" style={{ maxHeight: contentMaxHeight }}>
                                <table className="table table-default">
                                    <thead>
                                        <tr className="align-middle">
                                            <th className="w-5 text-center">
                                                <div className="form-check ">
                                                    <label >
                                                        <input ref={checkAllRef} className="form-check-input wh-20 checkAll" type="checkbox" disabled={isCompleted} onChange={handleCheckAllChange} />
                                                    </label>
                                                </div>
                                            </th>
                                            <th className="text-center w-5">{t('table.date')}</th>
                                            <th className="text-start w-5">{t('table.source')} </th>
                                            <th className="text-start">{t('table.description')} </th>
                                            <th className="text-start w-5">{t('table.status')} </th>
                                            {/* <th className="text-end w-5">{t('table.balance')}</th> */}
                                            <th className="text-end w-5">{t('table.debit')}</th>
                                            <th className="text-end w-5">{t('table.credit')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            fields.length > 0 ? (
                                                <>
                                                    {fields.map((data: any, index: any) => (
                                                        <tr className="align-middle" key={data['id']}>

                                                            <td className="text-center">
                                                                <div className="form-check ">
                                                                    <label>
                                                                        <input
                                                                            className="form-check-input wh-20 checkbox"
                                                                            {...register(`reconciles.${index}.checked`)}
                                                                            defaultChecked={fields[index]['checked']}
                                                                            disabled={isCompleted}
                                                                            type="checkbox"
                                                                            onChange={(e: any) => handleCheckAll(e, index)}
                                                                        />
                                                                    </label>
                                                                </div>
                                                            </td>

                                                            <td className="text-start">
                                                                <strong>{formatTransactionDate(fields[index].transaction_at)}</strong>
                                                            </td>

                                                            {/*  TYPE */}
                                                            <td className="text-start">
                                                                {records[index]?.txn_type == 1
                                                                    ? 'Deposit'
                                                                    : records[index]?.txn_type == 2
                                                                        ? 'Withdrawal'
                                                                        : t('common.na')}
                                                            </td>

                                                            <td className="text-start text-truncate"
                                                                style={{ maxWidth: '250px' }}
                                                                title={fields[index].description || t('common.na')}>
                                                                {fields[index].description || t('common.na')}
                                                            </td>

                                                            <td className="text-start">
                                                                {fields[index].posted ? 'Yes' : 'No'}
                                                            </td>

                                                            {/*  DEBIT */}
                                                            <td className="text-end">
                                                                {records[index]?.txn_type == 1
                                                                    ? usdCurrencyFormat(fields[index].account_value)
                                                                    : ''}
                                                            </td>

                                                            {/*  CREDIT */}
                                                            <td className="text-end">
                                                                {records[index]?.txn_type == 2
                                                                    ? usdCurrencyFormat(fields[index].account_value)
                                                                    : ''}
                                                            </td>
                                                        </tr>
                                                    ))}

                                                    {/*  TOTAL ROW ADDED */}
                                                    <tr className="align-middle fw-bold bg-light border-top">
                                                        <td colSpan={5} className="text-end">
                                                            Total
                                                        </td>

                                                        <td className="text-end">
                                                            {usdCurrencyFormat(totals.debit)}
                                                        </td>

                                                        <td className="text-end">
                                                            {usdCurrencyFormat(totals.credit)}
                                                        </td>
                                                    </tr>
                                                </>
                                            ) : (
                                                <tr>
                                                    {/* FIXED COLSPAN */}
                                                    <td colSpan={7} className='text-center'>
                                                        {t('message.noData')}
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </Form>
                    </div>

                </div>
            </div>

            <SAlert
                show={showConfirm}
                icon={'warning'}
                showCancel={true}
                confirmBtnText={t('button.reconcile_it')}
                cancelBtnText={t('button.cancel')}
                confirmBtnBsStyle={'danger'}
                cancelBtnBsStyle={"secondary"}
                title={t('confirm.title')}
                msg={t('confirm.noTransactionsMessage')}
                onConfirm={_handleConfirmation}
                onCancel={() => setShowConfirm(false)}
            />
        </div>

    )
}

export default BankReconcileTxn;